import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Button, Paper, TextField, IconButton, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { myApi } from '../../config/axiosConfig';
import useMediaQuery from '@mui/material/useMediaQuery';
import Swal from 'sweetalert2';
import BtnAcciones from '../../components/plantilla/tabla/BtnAcciones';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useRef } from "react";
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule } from 'ag-grid-community'; // ✅ Módulo de AG Grid
import img from "../../assets/img/spinner.svg";

// Iconos
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CrearEditarModal from './modales/CrearEditarMonterrey';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';

const CargasMonterrey = () => {
    const gridRef = useRef();
    const isNonMobile = useMediaQuery("(min-width:600px)");

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [loading, setLoading] = useState(false);

    const [unit, setUnit] = useState('');
    const [type, setType] = useState("TODO");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const handleChangeUnidad = (event) => setUnit(event.target.value);
    const handleChangeTipo = (event) => setType(event.target.value);

    const handleChangeStartDate = (newValue) => {
        setStartDate(newValue);
        if (endDate && newValue.isAfter(endDate)) {
            Swal.fire({
                icon: 'error',
                title: 'La fecha inicial no puede ser mayor que la fecha final.',
            }).then(() => setEndDate(newValue));
        }
    };

    const handleChangeEndDate = (newValue) => {
        setEndDate(newValue);
        if (startDate && newValue.isBefore(startDate)) {
            Swal.fire({
                icon: 'error',
                title: 'La fecha final no puede ser menor que la fecha inicial.',
            }).then(() => setEndDate(startDate));
        }
    };

    const handleClearDate = (fecha) => {
        if(fecha === 'fi') setStartDate(null);
        else if (fecha === 'ff') setEndDate(null);
    };

    const handleOpenModal = (carga = null) => {
        setSelected(carga);
        setIsEditing(!!carga);
        setModalOpen(true);
    };

    const handleDelete = useCallback(async (id) => {
        try {
            Swal.fire({
                title: '¿Estás seguro de eliminar esta carga?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: 'green',
                cancelButtonColor: 'red',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    let { data } = await myApi.delete(`/cargasFuel/deleteMonterrey/${id}`);
                    if (data.success) {
                        Swal.fire({ icon: 'success', title: 'Carga eliminada' });
                        setData(prevData => prevData.filter(carga => carga.id !== id));
                    } else {
                        Swal.fire({ icon: 'error', title: 'Error al eliminar la carga' });
                    }
                }
            });
        } catch (error) {
            console.error('Error deleting fuel up:', error);
        }
    }, []);

    const handleEdit = useCallback(async (fuelRow) => {
        handleOpenModal(data.find(carga => carga.id === fuelRow.id));
        setIsEditing(true);
        setSelected(fuelRow);
    }, [data]);

    const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        if (isNaN(d)) return "";

        const options = {
            year: '2-digit', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false,
        };

        const formattedDate = new Date(date).toLocaleString('en-US', options);
        const [datePart, timePart] = formattedDate.split(', ');
        const [month, day, year] = datePart.split('/');
        return `${month}/${day}/${year} ${timePart}`;
    };

    const columns = [
        { field: 'date', headerName: 'FECHA', headerTooltip: "FECHA", cellStyle: { textAlign: "center" }, valueFormatter: (params) => formatDate(params.value), pinned: true },
        { field: 'asset', headerName: 'UNIDAD', headerTooltip: "UNIDAD", cellStyle: { textAlign: "center" }, pinned: true },
        { field: 'asset_type', headerName: 'TIPO DE UNIDAD', headerTooltip: "TIPO DE UNIDAD", cellStyle: { textAlign: "center" } },
        { field: 'transportline', headerName: 'TRANSPORTISTA', headerTooltip: "TRANSPORTISTA", cellStyle: { textAlign: "center" } },
        { field: 'Asset_trsnport_line', headerName: 'LINEA', headerTooltip: "LINEA", cellStyle: { textAlign: "center" } },
        { field: 'driver', headerName: 'OPERADOR', headerTooltip: "OPERADOR", cellStyle: { textAlign: "center" }, valueGetter: (params) => params.data?.driver?.toUpperCase() || '' },
        { 
            field: 'liters', headerName: 'CANTIDAD LTS', headerTooltip: "CANTIDAD LTS", cellStyle: { textAlign: "right" }, minWidth: 100,
            valueGetter: (params) => {
                const raw = params.data?.liters;
                if (raw === null || raw === undefined) return null;
                const cleaned = raw.toString().replace(/,/g, '').replace('%', '').trim();
                if (cleaned === '' || cleaned.toUpperCase() === 'NULL') return null;
                const num = Number(cleaned);
                return Number.isFinite(num) ? num : null;
            },
            comparator: (a, b) => {
                if (a == null && b == null) return 0;
                if (a == null) return -1;
                if (b == null) return 1;
                return a - b;
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                const numericValue = Number(params.value.toString().replace(/,/g, ''));
                if (isNaN(numericValue)) return params.value;
                return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        },
        { 
            field: 'gallons', headerName: 'CANTIDAD GLLS', headerTooltip: "CANTIDAD GLLS", cellStyle: { textAlign: "right" }, minWidth: 100,
            valueGetter: (params) => {
                const raw = params.data?.gallons;
                if (raw === null || raw === undefined) return null;
                const cleaned = raw.toString().replace(/,/g, '').replace('%', '').trim();
                if (cleaned === '' || cleaned.toUpperCase() === 'NULL') return null;
                const num = Number(cleaned);
                return Number.isFinite(num) ? num : null;
            },
            comparator: (a, b) => {
                if (a == null && b == null) return 0;
                if (a == null) return -1;
                if (b == null) return 1;
                return a - b;
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                const numericValue = Number(params.value.toString().replace(/,/g, ''));
                if (isNaN(numericValue)) return params.value;
                return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        },
        { 
            field: 'price', headerName: 'PRECIO', headerTooltip: "PRECIO", cellStyle: { textAlign: "right" }, minWidth: 100,
            valueGetter: (params) => {
                const raw = params.data?.price;
                if (raw === null || raw === undefined) return null;
                const cleaned = raw.toString().replace(/,/g, '').replace('%', '').trim();
                if (cleaned === '' || cleaned.toUpperCase() === 'NULL') return null;
                const num = Number(cleaned);
                return Number.isFinite(num) ? num : null;
            },
            comparator: (a, b) => {
                if (a == null && b == null) return 0;
                if (a == null) return -1;
                if (b == null) return 1;
                return a - b;
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                const numericValue = Number(params.value.toString().replace(/,/g, ''));
                if (isNaN(numericValue)) return params.value;
                return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
        },
        { 
            field: 'total', headerName: 'MONTO', headerTooltip: "MONTO", cellStyle: { textAlign: "right" }, minWidth: 100,
            valueGetter: (params) => {
                const raw = params.data?.total;
                if (raw === null || raw === undefined) return null;
                const cleaned = raw.toString().replace(/,/g, '').replace('%', '').trim();
                if (cleaned === '' || cleaned.toUpperCase() === 'NULL') return null;
                const num = Number(cleaned);
                return Number.isFinite(num) ? num : null;
            },
            comparator: (a, b) => {
                if (a == null && b == null) return 0;
                if (a == null) return -1;
                if (b == null) return 1;
                return a - b;
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                const numericValue = Number(params.value.toString().replace(/,/g, ''));
                if (isNaN(numericValue)) return params.value;
                return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
        },
        { field: 'fuel_dispatcher', headerName: 'DESPACHADOR', headerTooltip: "DESPACHADOR", cellStyle: { textAlign: "center" }, valueGetter: (params) => params.data?.fuel_dispatcher?.toUpperCase() || '' },
        { field: 'move_type', headerName: 'IMPO/EXPO', headerTooltip: "IMPO/EXPO", cellStyle: { textAlign: "center" }, valueGetter: (params) => params.data?.move_type?.toUpperCase() || '' },
        { 
            field: 'tank_odometer', headerName: 'ODOMETRO TANQUE', headerTooltip: "ODOMETRO TANQUE", cellStyle: { textAlign: "center" }, minWidth: 120,
            valueGetter: (params) => {
                const raw = params.data?.tank_odometer;
                if (raw === null || raw === undefined) return null;
                const cleaned = raw.toString().replace(/,/g, '').replace('%', '').trim();
                if (cleaned === '' || cleaned.toUpperCase() === 'NULL') return null;
                const num = Number(cleaned);
                return Number.isFinite(num) ? num : null;
            },
            comparator: (a, b) => {
                if (a == null && b == null) return 0;
                if (a == null) return -1;
                if (b == null) return 1;
                return a - b;
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                const numericValue = Number(params.value.toString().replace(/,/g, ''));
                if (isNaN(numericValue)) return params.value;
                return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        },
        { 
            field: 'qty_by_odometer_calculated', headerName: 'ODOMETRO TANQUE CONTRA GLLS', headerTooltip: "ODOMETRO TANQUE CONTRA GLLS", cellStyle: { textAlign: "center" }, minWidth: 100,
            valueGetter: (params) => {
                const raw = params.data?.qty_by_odometer_calculated;
                if (raw === null || raw === undefined) return null;
                const cleaned = raw.toString().replace(/,/g, '').replace('%', '').trim();
                if (cleaned === '' || cleaned.toUpperCase() === 'NULL') return null;
                const num = Number(cleaned);
                return Number.isFinite(num) ? num : null;
            },
            comparator: (a, b) => {
                if (a == null && b == null) return 0;
                if (a == null) return -1;
                if (b == null) return 1;
                return a - b;
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                const numericValue = Number(params.value.toString().replace(/,/g, ''));
                if (isNaN(numericValue)) return params.value;
                return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        },
        { field: 'brand', headerName: 'MARCA', headerTooltip: "MARCA", cellStyle: { textAlign: "center" } },
        {
            headerName: "ACCIONES", headerTooltip: "ACCIONES", field: "actions", minWidth: 100, sortable: false, filter: false, suppressMenu: true, suppressColumnsToolPanel: true,
            cellRenderer: (params) => {
                const row = params.data;
                if (row.isTotal) return null;
                return (
                    <div style={{ display: "flex", gap: 8 }}>
                        <BtnAcciones icon={<BorderColorIcon />} label="Editar" onClick={() => handleEdit(params.data)} />
                        <BtnAcciones icon={<DeleteIcon />} label="Eliminar" onClick={() => handleDelete(params.data.id)} />
                    </div>
                );
            },
        }
    ];

    const fetch = async (unidad = null, fi = null, ff = null, tipo = null) => {
        setLoading(true);
        try {
            const datosEnviados = { unidad, fi, ff, tipo };
            const response = await myApi.post('/cargasFuel/getMonterrey', datosEnviados);
            if(response.data.success) setData(response.data.data);
            else setData([]);
        } catch(error) {
            console.error('Error fetching fuel loads:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateRow = (rowActualizada) => {
        setData(prev => prev.map(r => r.id === rowActualizada.id ? { ...r, ...rowActualizada } : r));
    };

    useEffect(() => { fetch(); }, []);

    const handleSubmit = async () => {
        let fechainicial = null, fechafinal = null, unidad = null, tipo = null;
        if (startDate && startDate.$d !== 'Invalid Date') fechainicial = startDate.$d.toJSON().substring(0, 10);
        if (endDate && endDate.$d !== 'Invalid Date') fechafinal = endDate.$d.toJSON().substring(0, 10);
        if (unit && unit !== 'undefined' && unit !== '') unidad = unit;
        if (type && type !== 'undefined' && type !== '') tipo = type;
        await fetch(unidad, fechainicial, fechafinal, tipo);
    };

    const totalsRow = React.useMemo(() => {
        const sum = (field) => data.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);
        const avg = (field) => {
            const valid = data.filter(r => Number(r[field]));
            if (!valid.length) return 0;
            return sum(field) / valid.length;
        };

        return {
            id: 'totals',
            asset: 'TOTAL',
            isTotal: true, 
            gallons: sum('gallons'),
            liters: sum('liters'),
            price: avg('price'),   
            total: sum('total'),
        };
    }, [data]);

    const onGridReady = (params) => {
        params.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (params) => {
        const columns = params.api.getColumns();
        const colsToAutoSize = columns
            .filter(col => {
                const def = col.getColDef();
                return !col.isPinned() && !def.cellRenderer;
            })
            .map(col => col.getColId());
            
        params.api.autoSizeColumns(colsToAutoSize, true);
    };

    const handleExport = () => {
        const columnDefs = gridRef.current.api.getColumnDefs();
        const columnasAExportar = columnDefs
            .filter(col => col.field && !['fotos', 'pc_file', 'actions'].includes(col.field))
            .map(col => col.field);

        gridRef.current.api.exportDataAsCsv({ columnKeys: columnasAExportar });
    };

    const addRow = (rowNueva) => {
        setData(prev => [rowNueva, ...prev]);
    };

    return (
        <>
            <Paper
                sx={{
                    p: 2,
                    height: "calc(100vh - 125px)", // ✅ Altura dinámica corregida
                    width: "100%",
                    maxWidth: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                }}
            >
                <Box sx={{ flexShrink: 0 }}>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <Grid container spacing={2} alignItems="center">
                            {/* Título y Botón Agregar */}
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Typography variant="h4" fontWeight={700}>
                                    Cargas de Monterrey
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: isNonMobile ? 'flex-end' : 'start' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddCircleIcon />}
                                    onClick={() => handleOpenModal()}
                                    sx={{ backgroundColor: 'black', marginBottom: 1 }}
                                >
                                    Agregar
                                </Button>
                            </Grid>

                            {/* Fila de Filtros + Botón BUSCAR alineado (Distribución 2.4/12 en pantallas medianas) */}
                            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                                <TextField label="Unidad" variant="outlined" value={unit} onChange={handleChangeUnidad} fullWidth size="small" />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker 
                                        value={startDate} 
                                        onChange={handleChangeStartDate} 
                                        format="MM/DD/YY" 
                                        label="Fecha Inicial" 
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }} 
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker 
                                        value={endDate} 
                                        onChange={handleChangeEndDate} 
                                        format="MM/DD/YY" 
                                        label="Fecha Final" 
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }} 
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel id="filtro-label">Filtro</InputLabel>
                                    <Select labelId="filtro-label" value={type} onChange={handleChangeTipo} label="Filtro" displayEmpty>
                                        <MenuItem value="TODO">TODO</MenuItem>
                                        <MenuItem value="CAJA">CAJA</MenuItem>
                                        <MenuItem value="TRACTOR">TRACTOR</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                                <Button
                                    type="button" 
                                    color="inherit" 
                                    onClick={handleSubmit} 
                                    variant="contained"
                                    startIcon={<ManageSearchIcon />} 
                                    fullWidth
                                    sx={{ color: "primary", fontWeight: "bold", height: '40px' }}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, mt: 1, display: "flex", flexDirection: "column" }}>
                    {loading ? (
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img alt="Cargando..." width="200px" height="200px" src={img} style={{ cursor: "pointer", borderRadius: "50%" }} />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ flexShrink: 0, mb: 1 }}>
                                <Button variant="contained" onClick={handleExport} sx={{ width: 'auto', minWidth: 120 }}>
                                    Exportar a Excel
                                </Button>
                            </Box>

                            <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                                <div className="ag-theme-quartz" style={{ height: "100%", width: "100%" }}>
                                    <AgGridReact
                                        modules={[AllCommunityModule]} // ✅ Módulo registrado correctamente
                                        ref={gridRef}
                                        rowData={data}
                                        columnDefs={columns}
                                        pagination={true}
                                        pinnedBottomRowData={[totalsRow]}
                                        getRowId={params => params.data.id}
                                        autoSizeStrategy={{ type: 'fitCellContents', skipHeader: true }}
                                        onGridReady={onGridReady}
                                        onFirstDataRendered={onFirstDataRendered}
                                    /> 
                                </div>
                            </Box>
                        </>
                    )}
                </Box>
            </Paper>

            <CrearEditarModal
                open={modalOpen} onClose={() => setModalOpen(false)}
                refresh={fetch} carga={selected} isEditing={isEditing}
                onUpdate={updateRow} onCreate={addRow}
            />
        </>
    );
};

export default CargasMonterrey;